import type { SelectedMaterialEvent, SelectedSpaceEvent, SelectedTaskEvent } from "~/store/eventsManagementSlice";

import {
	type GridRequestInput,
	ProductionMaterialEventStatus,
	ProductionSpaceEventStatus,
	ProductionTaskEventStatus
} from "~/gql/graphqlBase.generated";

export type EventDefinitionsTransformRequest = (request: GridRequestInput) => GridRequestInput;

export interface EventDefinitionSort {
	colId: string;
	sort: "asc" | "desc";
}

export const EVENT_DEFINITIONS_GRID_ID = "eventDefinitions";
export const EVENTS_PATH = "/produce/production/events";
export const MATERIAL_EVENTS_ENTITY_LABEL = "Material Events";
export const SPACE_EVENTS_ENTITY_LABEL = "Space Events";
export const TASK_EVENTS_ENTITY_LABEL = "Task Events";

// ProductionEventDefinitionType is internal in wsapi and never a GraphQL type, so it is declared here.
export const EVENT_DEFINITION_TYPES = {
	material: "MATERIAL",
	space: "SPACE",
	task: "TASK"
} as const;

export type EventDefinitionType = (typeof EVENT_DEFINITION_TYPES)[keyof typeof EVENT_DEFINITION_TYPES];

// wsapi canonicalizes status to its SCREAMING_SNAKE enum name; Deleted rows never reach the union.
export const EVENT_DEFINITION_ACTIVE_STATUS = "ACTIVE";

// Mirrors the registry entry's OutputColumns; the task-only labor columns are null on the other branches.
export interface EventDefinitionSsrmRow {
	createdAt: string | null;
	createdBy: string | null;
	detailCount: number | null;
	eventId: string;
	eventType: EventDefinitionType;
	laborTypeTagId: string | null;
	name: string;
	rowId: string;
	status: string;
	unitsPerHour: number | null;
	updatedAt: string | null;
	updatedBy: string | null;
}

export const isEventRowExpandable = (row: Pick<EventDefinitionSsrmRow, "detailCount"> | undefined): boolean =>
	row?.detailCount == null || row.detailCount > 0;

// Matches the registry entry's DefaultSort; name is one of the three FilterableCols, so it is sortable.
export const EVENT_DEFINITION_SORT_MODEL: EventDefinitionSort[] = [{ colId: "name", sort: "asc" }];

const eventTypeFilter = (eventType: EventDefinitionType) => ({
	eventType: { filterType: "set", values: [eventType] }
});

const activeStatusFilter = {
	status: { filterType: "set", values: [EVENT_DEFINITION_ACTIVE_STATUS] }
};

const withFilterOverrides =
	(overrides: object): EventDefinitionsTransformRequest =>
	(request: GridRequestInput): GridRequestInput => ({
		...request,
		filterModel: { ...(request.filterModel as object | null), ...overrides }
	});

// The server's FilterPushdownPrePipeline folds this predicate into the one matching union branch.
const withEventTypeFilter = (eventType: EventDefinitionType): EventDefinitionsTransformRequest =>
	withFilterOverrides(eventTypeFilter(eventType));

export const withActiveEventTypeFilter = (eventType: EventDefinitionType): EventDefinitionsTransformRequest =>
	withFilterOverrides({ ...eventTypeFilter(eventType), ...activeStatusFilter });

export const withLaborTypeTaskFilter = (laborTypeId: string): EventDefinitionsTransformRequest =>
	withFilterOverrides({
		...eventTypeFilter(EVENT_DEFINITION_TYPES.task),
		laborTypeTagId: { filterType: "set", values: [laborTypeId] }
	});

export const withEventIdsTypeFilter = (
	eventType: EventDefinitionType,
	eventIds: readonly string[]
): EventDefinitionsTransformRequest =>
	withFilterOverrides({ ...eventTypeFilter(eventType), eventId: { filterType: "set", values: [...eventIds] } });

export const MATERIAL_EVENT_TRANSFORM_REQUEST = withEventTypeFilter(EVENT_DEFINITION_TYPES.material);

export const toMaterialEventStatus = (status: string): ProductionMaterialEventStatus =>
	status === EVENT_DEFINITION_ACTIVE_STATUS
		? ProductionMaterialEventStatus.Active
		: ProductionMaterialEventStatus.Inactive;

export const toSelectedMaterialEventSnapshot = (event: EventDefinitionSsrmRow): SelectedMaterialEvent => ({
	name: event.name,
	productionMaterialEventId: event.eventId,
	status: toMaterialEventStatus(event.status)
});

export const SPACE_EVENT_TRANSFORM_REQUEST = withEventTypeFilter(EVENT_DEFINITION_TYPES.space);

export const toSpaceEventStatus = (status: string): ProductionSpaceEventStatus =>
	status === EVENT_DEFINITION_ACTIVE_STATUS ? ProductionSpaceEventStatus.Active : ProductionSpaceEventStatus.Inactive;

export const toSelectedSpaceEventSnapshot = (event: EventDefinitionSsrmRow): SelectedSpaceEvent => ({
	name: event.name,
	productionSpaceEventId: event.eventId,
	status: toSpaceEventStatus(event.status)
});

export const TASK_EVENT_TRANSFORM_REQUEST = withEventTypeFilter(EVENT_DEFINITION_TYPES.task);

export const taskEventTransformRequest = (laborTypeId?: string): EventDefinitionsTransformRequest =>
	laborTypeId ? withLaborTypeTaskFilter(laborTypeId) : TASK_EVENT_TRANSFORM_REQUEST;

export const toTaskEventStatus = (status: string): ProductionTaskEventStatus =>
	status === EVENT_DEFINITION_ACTIVE_STATUS ? ProductionTaskEventStatus.Active : ProductionTaskEventStatus.Inactive;

export const toSelectedTaskEventSnapshot = (event: EventDefinitionSsrmRow): SelectedTaskEvent => ({
	name: event.name,
	productionTaskEventId: event.eventId,
	status: toTaskEventStatus(event.status)
});

export default withEventTypeFilter;
